// Canonical session-download option definitions — the single source of
// truth Match Tracker and Reporting both consume for download type,
// endpoint, Accept header, unit support, and app applicability. Replaces
// MT's `SessionDownloadType` (types.ts) and Reporting's `ExportOption`
// (use-session-export.ts), which a later migration retires in favour of
// this module.
//
// Deliberately not re-exported through this domain's `index.ts` barrel —
// same precedent as `types.ts` — so importing it never pulls in `./api`'s
// RTK Query module graph. Import from this file's full path directly.

export const SESSION_DOWNLOAD_OPTIONS = [
  'events_json',
  'events_csv',
  'sportscode_xml',
  'player_metrics_csv',
  'session_metadata_csv',
  'physical_metrics_csv',
  'position_packets_parquet',
  'pitch_anchor_json',
  'session_players_json',
  'page_pdf',
  'session_pdf'
] as const

export type SessionDownloadOption = (typeof SESSION_DOWNLOAD_OPTIONS)[number]

export type SessionDownloadApp = 'mt' | 'reporting'

export interface SessionDownloadDefinition {
  /**
   * Broadcast-API path appended to `/game/session:{id}`. Absent when the
   * option has no dedicated file-download request of its own: `pitch_anchor_json`
   * and `session_players_json` are derived client-side from an already-fetched
   * session event, and `page_pdf`/`session_pdf` are non-file print actions.
   */
  endpoint?: string
  /** `Accept` header sent with the request. Absent alongside `endpoint`. */
  acceptHeader?: string
  /** Whether the endpoint honours a `units` query param on the request. */
  supportsUnit: boolean
  /** Which apps expose this download option. */
  apps: ReadonlyArray<SessionDownloadApp>
}

// `apps` is built from these two type-annotated constants, rather than an
// inline `as SessionDownloadApp[]` cast, on every entry below: under
// `satisfies`, an array literal like `['reporting']` infers as the narrow
// tuple `readonly ["reporting"]`, not `SessionDownloadApp[]`. Indexing this
// object by a `SessionDownloadOption` union (as the `apps:'mt'`/`supportsUnit`
// filters elsewhere do) then unions each entry's narrow `apps` type together,
// and `.includes('mt')` fails to typecheck against the single-app entries'
// narrower tuple type. A type annotation (unlike `as`) still checks the
// literal against `SessionDownloadApp[]` — a typo'd app name here would fail
// to compile — while widening the binding's type enough to avoid the tuple
// problem, so a cast that silently accepts a typo is unnecessary.
const BOTH_APPS: SessionDownloadApp[] = ['mt', 'reporting']
const REPORTING_ONLY: SessionDownloadApp[] = ['reporting']

// Retires an option from every app's download menu while keeping its
// definition (endpoint/accept header) intact for direct-API callers. The
// menus are built by filtering on `apps` (see MT_DOWNLOAD_OPTIONS /
// EXPORT_OPTIONS), so an empty list drops the option from both surfaces from
// this one place, rather than editing each app's list separately.
const NO_APPS: SessionDownloadApp[] = []

export const SESSION_DOWNLOAD_DEFINITIONS = {
  events_json: {
    // The customer events file. Defaults to CSV; the JSON variant is the same
    // route with `?format=json` (see downloadEventsJSON). The route pins its
    // own Content-Type, so `acceptHeader` is advisory only.
    endpoint: '/eventsData',
    acceptHeader: 'application/json',
    supportsUnit: true,
    apps: BOTH_APPS
  },
  events_csv: {
    endpoint: '/eventsData',
    acceptHeader: 'text/csv',
    supportsUnit: true,
    apps: BOTH_APPS
  },
  sportscode_xml: {
    endpoint: '/eventsSportsCode',
    acceptHeader: 'application/xml',
    supportsUnit: true,
    apps: BOTH_APPS
  },
  player_metrics_csv: {
    endpoint: '/playerMetrics',
    acceptHeader: 'text/csv',
    supportsUnit: true,
    apps: NO_APPS // not exposed in any menu; endpoint kept for direct-API callers
  },
  session_metadata_csv: {
    endpoint: '/sessionMetadata',
    acceptHeader: 'text/csv',
    supportsUnit: false,
    apps: BOTH_APPS
  },
  physical_metrics_csv: {
    endpoint: '/physicalMetrics',
    acceptHeader: 'text/csv',
    supportsUnit: true,
    apps: BOTH_APPS
  },
  position_packets_parquet: {
    endpoint: '/packets',
    acceptHeader: 'application/json',
    supportsUnit: false,
    apps: BOTH_APPS
  },
  pitch_anchor_json: {
    supportsUnit: false,
    apps: BOTH_APPS
  },
  session_players_json: {
    supportsUnit: false,
    apps: NO_APPS // not exposed in any menu; derived client-side, kept for direct callers
  },
  page_pdf: {
    supportsUnit: false,
    apps: REPORTING_ONLY
  },
  session_pdf: {
    supportsUnit: false,
    apps: REPORTING_ONLY
  }
} satisfies Record<SessionDownloadOption, SessionDownloadDefinition>

/**
 * Orders `options` by their index in `preference`. Options not listed in
 * `preference` sort after every listed one, in their original relative
 * order, rather than being dropped — so a future option that isn't yet
 * added to a caller's preference array still renders, just unordered.
 */
export function sortByDisplayPreference(
  options: SessionDownloadOption[],
  preference: SessionDownloadOption[]
): SessionDownloadOption[] {
  return [...options].sort((a, b) => {
    const ai = preference.indexOf(a)
    const bi = preference.indexOf(b)
    if (ai === -1 && bi === -1) return 0
    if (ai === -1) return 1
    if (bi === -1) return -1

    return ai - bi
  })
}
